import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Select,
  Form,
  Row,
  Col,
  Space,
  Typography,
  message,
  Button,
  Spin,
  Empty,
  Tag
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { 
  fetchFilterOptions, 
  fetchTransferStatus, 
  formatDateTime, 
  categorizeStatusData,
  type FilterOptions,
  type StatusRecord,
  type FilterParams
} from '../../utils/transferFeeApi';

const { Title, Text } = Typography;
const { Option } = Select;

// 本地接口定义
interface FilterForm {
  token?: string;
  chain?: string;
  exchange?: string;
}

// 默认筛选选项（API失败时的降级选项）
const defaultOptions: FilterOptions = {
  tokens: [],
  chains: [
    { value: 'ALL', label: '所有链 (All Chains)' }
  ],
  exchanges: [
    { value: 'ALL', label: '所有交易所 (All Exchanges)' }
  ]
};

const TransferFeeBoard: React.FC = () => {
  // 状态管理
  const [form] = Form.useForm();
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(defaultOptions);
  const [statusData, setStatusData] = useState<StatusRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  // 表格列定义
  const withdrawalPausedColumns: TableProps<StatusRecord>['columns'] = [
    {
      title: '交易所',
      dataIndex: 'exchange',
      key: 'exchange',
      width: 100,
      sorter: (a, b) => a.exchange.localeCompare(b.exchange),
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '币种',
      dataIndex: 'token',
      key: 'token',
      width: 80,
      sorter: (a, b) => a.token.localeCompare(b.token),
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: '链',
      dataIndex: 'chain',
      key: 'chain',
      width: 100,
      sorter: (a, b) => a.chain.localeCompare(b.chain),
      render: (text: string) => <Tag color="green">{text}</Tag>
    },
    {
      title: '状态说明',
      dataIndex: 'status_notes',
      key: 'status_notes',
      ellipsis: true,
      sorter: (a, b) => (a.status_notes || '').localeCompare(b.status_notes || ''),
      render: (text: string | null) => text || '-'
    },
    {
      title: '提现手续费',
      dataIndex: 'withdrawal_fee',
      key: 'withdrawal_fee',
      width: 120,
      sorter: (a, b) => (parseFloat(a.withdrawal_fee) || 0) - (parseFloat(b.withdrawal_fee) || 0),
      render: (text: string, record: StatusRecord) => `${text} ${record.token}`
    }
  ];

  const depositPausedColumns: TableProps<StatusRecord>['columns'] = [
    {
      title: '交易所',
      dataIndex: 'exchange',
      key: 'exchange',
      width: 100,
      sorter: (a, b) => a.exchange.localeCompare(b.exchange),
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '币种',
      dataIndex: 'token',
      key: 'token',
      width: 80,
      sorter: (a, b) => a.token.localeCompare(b.token),
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: '链',
      dataIndex: 'chain',
      key: 'chain',
      width: 100,
      sorter: (a, b) => a.chain.localeCompare(b.chain),
      render: (text: string) => <Tag color="green">{text}</Tag>
    },
    {
      title: '状态说明',
      dataIndex: 'status_notes',
      key: 'status_notes',
      ellipsis: true,
      sorter: (a, b) => (a.status_notes || '').localeCompare(b.status_notes || ''),
      render: (text: string | null) => text || '-'
    }
  ];

  const availableWithdrawalColumns: TableProps<StatusRecord>['columns'] = [
    {
      title: '交易所',
      dataIndex: 'exchange',
      key: 'exchange',
      width: 100,
      sorter: (a, b) => a.exchange.localeCompare(b.exchange),
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '币种',
      dataIndex: 'token',
      key: 'token',
      width: 80,
      sorter: (a, b) => a.token.localeCompare(b.token),
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: '链',
      dataIndex: 'chain',
      key: 'chain',
      width: 100,
      sorter: (a, b) => a.chain.localeCompare(b.chain),
      render: (text: string) => <Tag color="green">{text}</Tag>
    },
    {
      title: '提现手续费',
      dataIndex: 'withdrawal_fee',
      key: 'withdrawal_fee',
      width: 120,
      sorter: (a, b) => (parseFloat(a.withdrawal_fee) || 0) - (parseFloat(b.withdrawal_fee) || 0),
      defaultSortOrder: 'ascend',
      render: (text: string, record: StatusRecord) => `${text} ${record.token}`
    },
    {
      title: '最小提币量',
      dataIndex: 'min_withdrawal',
      key: 'min_withdrawal',
      width: 120,
      sorter: (a, b) => (parseFloat(a.min_withdrawal) || 0) - (parseFloat(b.min_withdrawal) || 0),
      render: (text: string, record: StatusRecord) => `${text} ${record.token}`
    },
    {
      title: '网络确认数',
      dataIndex: 'withdrawal_confirmations',
      key: 'withdrawal_confirmations',
      width: 100,
      sorter: (a, b) => a.withdrawal_confirmations - b.withdrawal_confirmations
    }
  ];

  const availableDepositColumns: TableProps<StatusRecord>['columns'] = [
    {
      title: '交易所',
      dataIndex: 'exchange',
      key: 'exchange',
      width: 100,
      sorter: (a, b) => a.exchange.localeCompare(b.exchange),
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '币种',
      dataIndex: 'token',
      key: 'token',
      width: 80,
      sorter: (a, b) => a.token.localeCompare(b.token),
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: '链',
      dataIndex: 'chain',
      key: 'chain',
      width: 100,
      sorter: (a, b) => a.chain.localeCompare(b.chain),
      render: (text: string) => <Tag color="green">{text}</Tag>
    },
    {
      title: '最小充币量',
      dataIndex: 'min_deposit',
      key: 'min_deposit',
      width: 120,
      sorter: (a, b) => (parseFloat(a.min_deposit) || 0) - (parseFloat(b.min_deposit) || 0),
      render: (text: string, record: StatusRecord) => `${text} ${record.token}`
    },
    {
      title: '网络确认数',
      dataIndex: 'deposit_confirmations',
      key: 'deposit_confirmations',
      width: 100,
      sorter: (a, b) => a.deposit_confirmations - b.deposit_confirmations
    }
  ];

  // 数据过滤
  const filteredData = useMemo(() => {
    return categorizeStatusData(statusData);
  }, [statusData]);

  // 获取筛选选项
  const fetchOptions = async () => {
    setOptionsLoading(true);
    try {
      const data = await fetchFilterOptions();
      setFilterOptions(data);
    } catch (error) {
      message.error('获取筛选选项失败，请稍后重试');
      console.error('Failed to fetch options:', error);
      // 失败时保持默认选项
      setFilterOptions(defaultOptions);
    } finally {
      setOptionsLoading(false);
    }
  };

  // 获取状态数据
  const fetchStatusData = async (filters: FilterForm) => {
    if (!filters.token) {
      message.warning('请先选择币种');
      return;
    }

    setLoading(true);
    try {
      const params: FilterParams = {
        token: filters.token,
        chain: filters.chain,
        exchange: filters.exchange
      };
      const response = await fetchTransferStatus(params);
      const dataWithKeys = response.data.map((item, index) => ({
        ...item,
        key: `${item.exchange}_${item.token}_${item.chain}_${index}`
      }));
      setStatusData(dataWithKeys);
      setLastUpdated(response.last_updated_timestamp);
    } catch (error) {
      message.error('获取状态数据失败，请检查网络连接');
      console.error('Failed to fetch status data:', error);
      setStatusData([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理表单值变化
  const handleFormChange = (changedValues: Partial<FilterForm>, allValues: FilterForm) => {
    // 当任何筛选条件变化时，自动触发查询
    if (allValues.token) {
      fetchStatusData(allValues);
    }
  };

  // 手动刷新
  const handleRefresh = () => {
    const currentValues = form.getFieldsValue();
    if (currentValues.token) {
      fetchStatusData(currentValues);
    } else {
      message.warning('请先选择币种');
    }
  };

  // 初始化
  useEffect(() => {
    fetchOptions();
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          交易所充提币状态看板
        </Title>
        <Text type="secondary">
          查询和展示不同加密货币在多个交易所的充值和提现状态
        </Text>
      </div>

      {/* 筛选区域 */}
      <Card title="筛选条件" style={{ marginBottom: '24px' }}>
        <Spin spinning={optionsLoading}>
          <Form
            form={form}
            layout="inline"
            onValuesChange={handleFormChange}
            initialValues={{ chain: 'ALL', exchange: 'ALL' }}
          >
            <Form.Item
              name="token"
              label="币种"
              rules={[{ required: true, message: '请选择币种' }]}
            >
              <Select
                showSearch
                placeholder="输入或选择币种"
                optionFilterProp="children"
                style={{ width: 200 }}
                allowClear
              >
                {filterOptions.tokens.map(token => (
                  <Option key={token.value} value={token.value}>
                    {token.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="chain" label="链">
              <Select 
                showSearch
                placeholder="输入或选择链"
                optionFilterProp="children"
                style={{ width: 250 }}
                allowClear
              >
                {filterOptions.chains.map(chain => (
                  <Option key={chain.value} value={chain.value}>
                    {chain.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="exchange" label="交易所">
              <Select placeholder="选择交易所" style={{ width: 250 }}>
                {filterOptions.exchanges.map(exchange => (
                  <Option key={exchange.value} value={exchange.value}>
                    {exchange.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                刷新
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>

             {/* 更新时间显示 */}
       {lastUpdated && (
         <div style={{ marginBottom: '16px', textAlign: 'right' }}>
           <Text type="secondary">
             最后更新时间: {formatDateTime(lastUpdated)}
           </Text>
         </div>
       )}

      {/* 数据展示区域 */}
      <Row gutter={[16, 16]}>
        {/* 当前可提币列表 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>当前可提币列表 (交易所-&gt;链上)</span>
                <Tag color="green">{filteredData.availableWithdrawal.length}</Tag>
              </Space>
            }
            size="small"
          >
            <Table
              columns={availableWithdrawalColumns}
              dataSource={filteredData.availableWithdrawal}
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ y: 300, x: 'max-content' }}
              locale={{
                emptyText: filteredData.availableWithdrawal.length === 0 && !loading ? 
                  <Empty description="暂无可提币的记录" /> : undefined
              }}
            />
          </Card>
        </Col>

        {/* 当前可充值列表 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>当前可充值列表 (链上-&gt;交易所)</span>
                <Tag color="green">{filteredData.availableDeposit.length}</Tag>
              </Space>
            }
            size="small"
          >
            <Table
              columns={availableDepositColumns}
              dataSource={filteredData.availableDeposit}
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ y: 300, x: 'max-content' }}
              locale={{
                emptyText: filteredData.availableDeposit.length === 0 && !loading ? 
                  <Empty description="暂无可充值的记录" /> : undefined
              }}
            />
          </Card>
        </Col>

        {/* 暂停提币列表 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>暂停提币列表</span>
                <Tag color="red">{filteredData.withdrawalPaused.length}</Tag>
              </Space>
            }
            size="small"
          >
            <Table
              columns={withdrawalPausedColumns}
              dataSource={filteredData.withdrawalPaused}
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ y: 300, x: 'max-content' }}
              locale={{
                emptyText: filteredData.withdrawalPaused.length === 0 && !loading ? 
                  <Empty description="暂无暂停提币的记录" /> : undefined
              }}
            />
          </Card>
        </Col>

        {/* 暂停充值列表 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>暂停充值列表</span>
                <Tag color="red">{filteredData.depositPaused.length}</Tag>
              </Space>
            }
            size="small"
          >
            <Table
              columns={depositPausedColumns}
              dataSource={filteredData.depositPaused}
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ y: 300, x: 'max-content' }}
              locale={{
                emptyText: filteredData.depositPaused.length === 0 && !loading ? 
                  <Empty description="暂无暂停充值的记录" /> : undefined
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TransferFeeBoard; 